import type { UserActivityType } from "@/api/types";
import { verifyRoles } from "@/utils/verifyRoles";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { z } from "zod/v4";
import { ActivitiesDashboard } from "../../../features/activities";
import { isomorphicHttpClient } from "../../../httpClient";

const activitiesFiltersSearchParamsSchema = z.object({
  userId: z.string().optional().catch(undefined),
  organizationId: z.string().optional().catch(undefined),
  type: z
    .array(
      z.enum([
        "auth",
        "featureFlags",
        "internalCredentials",
        "organizations",
        "tenants",
        "users",
      ] as const satisfies UserActivityType[]),
    )
    .default([])
    .catch([]),
  startDate: z.iso.datetime().optional().catch(undefined),
  endDate: z.iso.datetime().optional().catch(undefined),
});

export const Route = createFileRoute("/dashboard/activities/")({
  component: ActivitiesDashboard,
  head: () => ({
    meta: [{ title: "Atividades - Backoffice Detentora Iniciador" }],
  }),
  validateSearch: activitiesFiltersSearchParamsSchema,
  search: {
    middlewares: [
      stripSearchParams(activitiesFiltersSearchParamsSchema.parse({})),
    ],
  },
  beforeLoad: async ({ context: { queryClient } }) => {
    const session = await verifyRoles([
      "INIC_ADMIN",
      "ORG_ADMIN",
      "ORG_OPERATOR",
    ]);

    const {
      me: { role },
    } = session;

    queryClient.prefetchQuery({
      queryKey: ["users"],
      queryFn: async () => {
        const httpClient = isomorphicHttpClient();

        const { data, error } = await httpClient.GET("/v1/users");

        if (error) {
          throw new Error(error.message);
        }

        return data;
      },
    });

    if (role === "INIC_ADMIN") {
      queryClient.prefetchQuery({
        queryKey: ["organizations"],
        queryFn: async () => {
          const httpClient = isomorphicHttpClient();

          const { data, error } = await httpClient.GET("/v1/organizations");

          if (error) {
            throw new Error(error.message);
          }

          return data;
        },
      });
    }

    return session;
  },
});
